import { Injectable } from '@angular/core';
import type { User, CountryDetail, RawMerchantAccount } from '../models';

const REFRESH_TOKEN_KEY = 'gozem_refresh_token';
const REFRESH_TOKEN_EXPIRY_KEY = 'gozem_refresh_token_expiry';
const USER_DATA_KEY = 'gozem_user_data';
const COUNTRY_DETAIL_KEY = 'gozem_country_detail';
const ACCOUNTS_KEY = 'gozem_accounts';
const SELECTED_ACCOUNT_ID_KEY = 'gozem_selected_account_id';
const TOKEN_KEY = 'gozem_token';
const MERCHANT_ID_KEY = 'gozem_merchant_id';
const MERCHANT_USER_ID_KEY = 'gozem_merchant_user_id';

@Injectable({
  providedIn: 'root',
})
export class Storage {
  private readonly storage: globalThis.Storage;

  constructor() {
    this.storage =
      globalThis.window === undefined ? ({} as globalThis.Storage) : globalThis.localStorage;
  }

  set(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  }

  get(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      console.error('Error reading from storage:', error);
      return null;
    }
  }

  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error('Error removing from storage:', error);
    }
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  /**
   * Store refresh token with expiration time
   */
  setRefreshToken(token: string, expiresInSeconds: number): void {
    try {
      const expiryTime = Date.now() + expiresInSeconds * 1000;
      this.storage.setItem(REFRESH_TOKEN_KEY, token);
      this.storage.setItem(REFRESH_TOKEN_EXPIRY_KEY, expiryTime.toString());
    } catch (error) {
      console.error('Error saving refresh token:', error);
    }
  }

  /**
   * Get refresh token if it exists and is not expired
   */
  getRefreshToken(): string | null {
    try {
      const token = this.storage.getItem(REFRESH_TOKEN_KEY);
      const expiry = this.storage.getItem(REFRESH_TOKEN_EXPIRY_KEY);

      if (!token || !expiry) {
        return null;
      }

      // Check if token is expired
      if (Date.now() >= Number.parseInt(expiry, 10)) {
        this.clearRefreshToken();
        return null;
      }

      return token;
    } catch (error) {
      console.error('Error reading refresh token:', error);
      return null;
    }
  }

  /**
   * Clear refresh token from storage
   */
  clearRefreshToken(): void {
    try {
      this.storage.removeItem(REFRESH_TOKEN_KEY);
      this.storage.removeItem(REFRESH_TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error('Error clearing refresh token:', error);
    }
  }

  /**
   * Check if refresh token exists and is valid
   */
  hasValidRefreshToken(): boolean {
    return this.getRefreshToken() !== null;
  }

  /**
   * Store user session data (user, country details, and accounts)
   * Used to restore full session after page refresh
   */
  setUserData(
    user: User,
    countryDetail: CountryDetail,
    accounts: readonly RawMerchantAccount[],
    token: string,
    merchantId: string,
    merchantUserId: string,
  ): void {
    try {
      this.storage.setItem(USER_DATA_KEY, JSON.stringify(user));
      this.storage.setItem(COUNTRY_DETAIL_KEY, JSON.stringify(countryDetail));
      this.storage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
      this.storage.setItem(TOKEN_KEY, token);
      this.storage.setItem(MERCHANT_ID_KEY, merchantId);
      this.storage.setItem(MERCHANT_USER_ID_KEY, merchantUserId);
    } catch (error) {
      console.error('Error saving user data:', error);
    }
  }

  /**
   * Get stored user session data
   * Returns null if any required data is missing
   */
  getUserData(): {
    user: User;
    countryDetail: CountryDetail;
    accounts: RawMerchantAccount[];
    token: string;
    merchantId: string;
    merchantUserId: string;
  } | null {
    try {
      const userStr = this.storage.getItem(USER_DATA_KEY);
      const countryStr = this.storage.getItem(COUNTRY_DETAIL_KEY);
      const accountsStr = this.storage.getItem(ACCOUNTS_KEY);
      const token = this.storage.getItem(TOKEN_KEY);
      const merchantId = this.storage.getItem(MERCHANT_ID_KEY);
      const merchantUserId = this.storage.getItem(MERCHANT_USER_ID_KEY);

      if (!userStr || !countryStr || !accountsStr || !token || !merchantId || !merchantUserId) {
        return null;
      }

      return {
        user: JSON.parse(userStr),
        countryDetail: JSON.parse(countryStr),
        accounts: JSON.parse(accountsStr),
        token,
        merchantId,
        merchantUserId,
      };
    } catch (error) {
      console.error('Error reading user data:', error);
      return null;
    }
  }

  /**
   * Clear user session data from storage
   */
  clearUserData(): void {
    try {
      this.storage.removeItem(USER_DATA_KEY);
      this.storage.removeItem(COUNTRY_DETAIL_KEY);
      this.storage.removeItem(ACCOUNTS_KEY);
      this.storage.removeItem(TOKEN_KEY);
      this.storage.removeItem(MERCHANT_ID_KEY);
      this.storage.removeItem(MERCHANT_USER_ID_KEY);
    } catch (error) {
      console.error('Error clearing user data:', error);
    }
  }

  /**
   * Check if user data exists in storage
   */
  hasUserData(): boolean {
    return this.getUserData() !== null;
  }

  /**
   * Store the selected account ID
   */
  setSelectedAccountId(accountId: string): void {
    try {
      this.storage.setItem(SELECTED_ACCOUNT_ID_KEY, accountId);
    } catch (error) {
      console.error('Error saving selected account ID:', error);
    }
  }

  /**
   * Get the selected account ID
   */
  getSelectedAccountId(): string | null {
    try {
      return this.storage.getItem(SELECTED_ACCOUNT_ID_KEY);
    } catch (error) {
      console.error('Error reading selected account ID:', error);
      return null;
    }
  }

  /**
   * Clear the selected account ID
   */
  clearSelectedAccountId(): void {
    try {
      this.storage.removeItem(SELECTED_ACCOUNT_ID_KEY);
    } catch (error) {
      console.error('Error clearing selected account ID:', error);
    }
  }

  /**
   * Get all stored accounts
   */
  getAccounts(): RawMerchantAccount[] | null {
    try {
      const accountsStr = this.storage.getItem(ACCOUNTS_KEY);
      if (!accountsStr) {
        return null;
      }
      return JSON.parse(accountsStr);
    } catch (error) {
      console.error('Error reading accounts:', error);
      return null;
    }
  }
}
